import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const SearchForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [lookups, setLookups] = useState({});
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [error, setError] = useState(null);

  // Default form data
  const defaultFormData = {
    sort: 'recent',
    age_min: 18,
    age_max: 99,
    distance: 'any',
    online: false,
    photos: false,
    // Location parameters
    latitude: '',
    longitude: '',
    city: '',
    region: '',
    country: '',
    // Filter parameters
    sex: [],
    orientation: [],
    looking_to: [],
    eyes: [],
    body_type: [],
    ethnicity: [],
    income: [],
    net_worth: [],
    children: [],
    relationship_status: [],
    smoker: [],
    drinker: [],
    pot_friendly: [],
    party_drugs: [],
    keywords: ''
  };

  const [formData, setFormData] = useState(defaultFormData);

  // Fetch lookups for filter options
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        // Fetch all lookups
        const response = await api.get('/lookups');
        const lookupData = {};

        if (response.data && response.data.data) {
          response.data.data.forEach(lookup => {
            lookupData[lookup.name] = lookup.choices;
          });
        }

        setLookups(lookupData);
      } catch (error) {
        console.error('Error fetching lookups:', error);
        setError('Failed to load search options. Please try again later.');
      } finally {
        setLoadingLookups(false);
      }
    };

    fetchLookups();
  }, []);

  // Fetch filter values from search endpoint
  useEffect(() => {
    const fetchFilterValues = async () => {
      try {
        // Fetch default search values from the API
        const response = await api.get('/search');
        console.log('Search API response:', response.data);

        if (response.data && response.data.search) {
          const searchData = response.data.search;

          // Extract filter values from the search response
          if (searchData.filters) {
            const filters = searchData.filters;
            const updatedFormData = { ...defaultFormData };

            // Apply filter values to form data
            Object.keys(filters).forEach(key => {
              if (key in updatedFormData) {
                updatedFormData[key] = filters[key];
              }
            });

            // Handle sort separately
            if (searchData.sort) {
              updatedFormData.sort = searchData.sort;
            }

            console.log('Applied filter values from API:', updatedFormData);
            setFormData(updatedFormData);
          }
        }
      } catch (error) {
        console.error('Error fetching search filters:', error);
        // Don't set error state here, as we can still use default values
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchFilterValues();
  }, []);

  // Get user's current location for distance-based searches
  useEffect(() => {
    // Check if the browser supports geolocation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Update form data with user's coordinates
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          }));
          console.log('Location obtained:', position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleMultiSelectChange = (e) => {
    const { name, value, checked } = e.target;

    setFormData(prev => {
      const currentValues = [...prev[name]];

      if (checked) {
        return {
          ...prev,
          [name]: [...currentValues, value]
        };
      } else {
        return {
          ...prev,
          [name]: currentValues.filter(val => val !== value)
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare the search parameters
      const searchParams = { ...formData };

      // Convert boolean values to strings for API compatibility
      if (searchParams.online) searchParams.online = 'true';
      if (searchParams.photos) searchParams.photos = 'true';

      // Add recent flag to save this search as the most recent search
      searchParams.recent = 'true';

      // Navigate to results page with search parameters
      navigate('/search/results', { state: { searchParams } });
    } catch (error) {
      console.error('Error submitting search:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loadingLookups || loadingFilters) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading search options...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error}
        </Alert>
        <div className="text-center mt-4">
          <Button
            variant="primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4 text-center">Find Your Perfect Match</h2>

      <Form onSubmit={handleSubmit}>
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-4">
            <h5 className="fw-bold mb-3">Basic Filters</h5>

            <Row>
              <Col md={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>Sort By</Form.Label>
                  <Form.Select
                    name="sort"
                    value={formData.sort}
                    onChange={handleChange}
                  >
                    <option value="recent">Recently Active</option>
                    <option value="newest">Newest Members</option>
                    <option value="distance">Distance</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>Distance (miles)</Form.Label>
                  <Form.Select
                    name="distance"
                    value={formData.distance}
                    onChange={handleChange}
                  >
                    <option value="10">10 miles</option>
                    <option value="25">25 miles</option>
                    <option value="50">50 miles</option>
                    <option value="100">100 miles</option>
                    <option value="any">Any distance</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>Min Age</Form.Label>
                  <Form.Select
                    name="age_min"
                    value={formData.age_min}
                    onChange={handleChange}
                  >
                    {[...Array(63)].map((_, i) => (
                      <option key={i} value={i + 18}>{i + 18}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} lg={3} className="mb-3">
                <Form.Group>
                  <Form.Label>Max Age</Form.Label>
                  <Form.Select
                    name="age_max"
                    value={formData.age_max}
                    onChange={handleChange}
                  >
                    {[...Array(63)].map((_, i) => (
                      <option key={i} value={i + 18}>{i + 18}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md={6} className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="online"
                  name="online"
                  label="Online Now"
                  checked={formData.online}
                  onChange={handleChange}
                />
              </Col>

              <Col md={6} className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="photos"
                  name="photos"
                  label="Has Photos"
                  checked={formData.photos}
                  onChange={handleChange}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-4">
            <h5 className="fw-bold mb-3">Advanced Filters</h5>

            <Row>
              <Col md={6} lg={4} className="mb-4">
                <Form.Group>
                  <Form.Label>Gender</Form.Label>
                  {lookups.sex && lookups.sex.map(option => (
                    <Form.Check
                      key={option.id}
                      type="checkbox"
                      id={`sex-${option.id}`}
                      name="sex"
                      value={option.id}
                      label={option.value}
                      checked={formData.sex.includes(option.id.toString())}
                      onChange={handleMultiSelectChange}
                      className="mb-2"
                    />
                  ))}
                </Form.Group>
              </Col>

              <Col md={6} lg={4} className="mb-4">
                <Form.Group>
                  <Form.Label>Orientation</Form.Label>
                  {lookups.orientation && lookups.orientation.map(option => (
                    <Form.Check
                      key={option.id}
                      type="checkbox"
                      id={`orientation-${option.id}`}
                      name="orientation"
                      value={option.id}
                      label={option.value}
                      checked={formData.orientation.includes(option.id.toString())}
                      onChange={handleMultiSelectChange}
                      className="mb-2"
                    />
                  ))}
                </Form.Group>
              </Col>

              <Col md={6} lg={4} className="mb-4">
                <Form.Group>
                  <Form.Label>Looking To</Form.Label>
                  {lookups.looking_to && lookups.looking_to.map(option => (
                    <Form.Check
                      key={option.id}
                      type="checkbox"
                      id={`looking_to-${option.id}`}
                      name="looking_to"
                      value={option.id}
                      label={option.value}
                      checked={formData.looking_to.includes(option.id.toString())}
                      onChange={handleMultiSelectChange}
                      className="mb-2"
                    />
                  ))}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Keywords</Form.Label>
                  <Form.Control
                    type="text"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    placeholder="Search by keywords in profiles"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-grid gap-2 d-md-flex justify-content-md-center mb-5">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="px-5"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Searching...
              </>
            ) : (
              <>
                <i className="bi bi-search me-2"></i>
                Search
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline-secondary"
            size="lg"
            className="px-5"
            onClick={() => setFormData(defaultFormData)}
          >
            Reset
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default SearchForm;
